import React, { useState, useContext, useEffect, useRef } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import {localizeKey, localizeArt} from './../localize.js';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import Collapsible from './Collapsible';
import ImageScroller from './ImageScroller';
import { Link } from 'react-router-dom';
import PlatformRatings from './PlatformRatings';
import countries from 'i18n-iso-countries';
import CardEventArtChatMini from './CardEventArtChatMini.js';
import FollowingDropdown from './FollowingModal.js';
import LogbookDropdown from './LogbookDropdown'; // Import LogbookDropdown
import { formatYear, formatDate } from './../utils.js'; // Import the global function
import { Button } from "@mantine/core";

// Initialize country data for localization
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
countries.registerLocale(require("i18n-iso-countries/langs/ru.json"));

const ArtPage = ({ artId, handleLoginWithGoogle }) => {
  // const navigate = useNavigate();
  const myUser = useContext(MyUserContext);

  const { id: pathId } = useParams();
  const id = pathId || artId; // Use artId as fallback if pathId is not provided

  const queryParams = new URLSearchParams(window.location.search);
  const link = queryParams.get('link');

  const [art, setArt] = useState(null);
  const [similars, setSimilars] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false); // State to control image viewer visibility
  const [currentImageIndex, setCurrentImageIndex] = useState(0); // State to track the current image index
  const [isCopyLinkCopied, setIsCopyLinkCopied] = useState(false);
  const copyLinkTimeoutRef = useRef(null);


  useEffect(() => {
    if (id) {
      // setArt(null); // Reset art state
      // setSimilars(null); // Reset similars state
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      (async () => {
        await fetchArt();
        // After 2 seconds, fetch art again to get updated data (descriptions, images, etc.)
        setTimeout(() => {
          fetchArt();
        }, 2000);
        fetchSimilars(id);
        fetchLogbook(id);
      })();
    } else {
      handleArtSubmit(link);
      // Similars and log book will be fetch right after art is loaded.
    }
  }, [id]); // id (and not []) is to force redrawing when navigating between different arts

  useEffect(() => {
    return () => {
      if (copyLinkTimeoutRef.current) {
        clearTimeout(copyLinkTimeoutRef.current);
      }
    };
  }, []);

  const [isDropdownVisible, setIsDropdownVisible] = useState(false); // users, recommendations
  const [isLogbookDropdownVisible, setIsLogbookDropdownVisible] = useState(false); // State for dropdown visibility
  const [initialLogbookDateTimes, setInitialLogbookDateTimes] = useState(null); // State for pre-loaded logbook date times

  const fetchArt = () => {
    return axiosInstance
      .get(`/arts/${id}/`)
      .then((response) => {
        setArt(response.data);
        return response.data;
      })
      .catch((error) => {
        console.error(error);
        return null;
      });
  };

  const fetchLogbook = (artID) => {
    if (myUser) {
      axiosInstance
        .get(`/art/logbook/get?artId=${artID}&userId=${myUser.myUserInfo.myUserId}`)
        .then((response) => {
          setInitialLogbookDateTimes(response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const handleArtSubmit = (link) => {
    axiosInstance
    .post(`/arts`, { wikiArticleLink: link })
    .then((response) => {
      setArt(response.data);
      fetchLogbook(response.data.id);
      fetchSimilars(response.data.id);

      // Replace the current URL in the browser history
      window.history.replaceState({}, '', `/arts/${response.data.id}`);
    })
    .catch((error) => {
      console.error(error);
    });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setIsCopyLinkCopied(true);
      if (copyLinkTimeoutRef.current) {
        clearTimeout(copyLinkTimeoutRef.current);
      }
      copyLinkTimeoutRef.current = setTimeout(() => {
        setIsCopyLinkCopied(false);
      }, 800);
    } catch (error) {
      console.error(error);
    }
  };

  const [loadingSimilarsManual, setLoadingSimilarsManual] = useState(false);

  const fetchSimilars = (artID, count = 60) => {
    setLoadingSimilarsManual(true);
    axiosInstance
    .get(`/artsSimilar/${artID}/?count=${count}`)
    .then((response) => {
      setSimilars(response.data);
      setLoadingSimilarsManual(false);
    })
    .catch((error) => {
      console.error(error);
      setLoadingSimilarsManual(false);
    })
  }

  // const handleEventTypeMove = (newEventType) => {
  //   axiosInstance
  //   .post(`/artEvents`, { artId: art.id, type: newEventType })
  //   .then((response) => {
  //     setArt(response.data.art);
  //     navigate("/artEvents/"+response.data.art.myEventId+"?tagsInitFocus=true");
  //   })
  //   .catch((error) => {
  //     console.error(error);
  //   });
  // };

  const formatDuration = (minutes, language) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return language === "ru"
      ? `${hours}ч ${mins} мин`
      : `${hours}h ${mins} min`;
  };

  // Video start.

  // Video viewer state + helpers
  const [isVideoViewerOpen, setIsVideoViewerOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  const videos = art ? (art.videoUrls || []) : [];

  // Detect YouTube link and return embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url);
      // youtu.be short link
      if (u.hostname === 'youtu.be') {
        const id = u.pathname.slice(1);
        return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
      }
      // youtube.com (watch?v=)
      if (u.hostname.includes('youtube.com')) {
        const v = u.searchParams.get('v');
        if (v) return `https://www.youtube.com/embed/${v}?autoplay=1&rel=0`;
        // handle /embed/ or other formats by returning the path after /embed/
        const pathParts = u.pathname.split('/');
        const embedIndex = pathParts.indexOf('embed');
        if (embedIndex !== -1 && pathParts[embedIndex + 1]) {
          return `https://www.youtube.com/embed/${pathParts[embedIndex + 1]}?autoplay=1&rel=0`;
        }
      }
    } catch (e) {
      // not a valid URL string — fallthrough
    }
    return null;
  };

  // quick heuristic to see if url is a direct playable video file
  const isDirectVideoFile = (url) => {
    if (!url) return false;
    return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
  };

  const openVideoViewer = (index) => {
    // Hack youtube links to open in app if possible.
    // const current = videos[index];
    // const ytEmbed = getYouTubeEmbedUrl(current);
    // if (ytEmbed) {
    //   const watchUrl = ytEmbed.replace("/embed/", "/watch?v=");
    //   window.location.href = `youtube://www.youtube.com${watchUrl.replace("https://www.youtube.com", "")}`;
    //   setTimeout(() => window.open(watchUrl, "_blank"), 500);
    //   return;
    // }

    setCurrentVideoIndex(index);
    setIsVideoViewerOpen(true);
  };

  const closeVideoViewer = () => {
    setIsVideoViewerOpen(false);
  };

  const showNextVideo = () => {
    if (!videos || videos.length === 0) return;
    setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videos.length);
  };

  const showPreviousVideo = () => {
    if (!videos || videos.length === 0) return;
    setCurrentVideoIndex((prevIndex) => (prevIndex - 1 + videos.length) % videos.length);
  };

  // Video end.

  const images = art ? (art.imageUrls && art.imageUrls.length > 0 ? art.imageUrls : [art.thumbnailUrl]) : [];

  const preloadImages = (startIndex, count) => {
    if (images) {
      for (let i = 0; i < count; i++) {
        const index = (startIndex + i) % images.length;
        const img = new Image();
        img.src = images[index];
      }
      // Prev direction too.
      for (let i = 0; i < count; i++) {
        const index = startIndex - i >= 0 ? startIndex - i : images.length + (startIndex - i);
        const img = new Image();
        img.src = images[index];
      }
    }
  };

  const openImageViewer = (index) => {
    setCurrentImageIndex(index);
    setIsViewerOpen(true);
  };

  const closeImageViewer = () => {
    setIsViewerOpen(false);
  };

  const showNextImage = () => {
    setCurrentImageIndex((prevIndex) => {
      const newIndex = (prevIndex + 1) % (images.length);
      preloadImages(newIndex, 2); // Preload the next 3 images
      return newIndex;
    });
  };

  const showPreviousImage = () => {
    setCurrentImageIndex((prevIndex) => {
      const newIndex = (prevIndex - 1 + (images.length || 1)) % (images.length || 1);
      preloadImages(newIndex, 2); // Preload the next 3 images
      return newIndex;
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      preloadImages(currentImageIndex, 5);
    }, 1000); // small delay ensures component fully loaded first
    return () => clearTimeout(timer);
  }, [currentImageIndex, art]);

  const renderDescription = (description) => {
    if (!description || description === '') {
      return null;
    }

    // Split by existing paragraphs (\r or \n)
    const paragraphs = description.split(/\r?\n/);

    // Process each paragraph
    const processedParagraphs = paragraphs.flatMap((paragraph) => {
      const sentences = paragraph.match(/[^.!?]+[.!?]+/g) || [paragraph]; // Split into sentences
      const newParagraphs = [];
      let currentParagraph = '';

      sentences.forEach((sentence) => {
        if ((currentParagraph + sentence).length > 250) {
          newParagraphs.push(currentParagraph.trim());
          currentParagraph = sentence;
        } else {
          currentParagraph += sentence;
        }
      });

      if (currentParagraph) {
        newParagraphs.push(currentParagraph.trim());
      }

      return newParagraphs;
    });

    // Join processed paragraphs with two <br/>
    return processedParagraphs.map((p, index) => (
      <React.Fragment key={index}>
        {p}
        {index < processedParagraphs.length - 1 && (
          <>
            <br />
            <br />
          </>
        )}
      </React.Fragment>
    ));
  };

  if (!art) {
    return (
      <>
      </>
    );
  }

  const language = myUser ? myUser.myUserSettings.lang : 'ru';

  return (
    <div 
      key={art.id} 
      class="container mx-auto mt-4 md:mt-8 px-4 md:px-8 w-full no-scrollbar overflow-hidden"
    >
        <div className="flex justify-center relative">
          <div className="relative inline-block"> {/* make wrapper shrink to image */}
            <img
              className="max-h-96 cursor-pointer block"
              src={art.thumbnailUrl}
              onClick={() => openImageViewer(0)}
            />
            {videos && videos.length > 0 && (
              <div
                className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-sm px-2 py-1 rounded cursor-pointer hover:bg-opacity-90 flex items-center space-x-1"
                style={{ maxWidth: '100%' }} // ensure it never exceeds img width
                onClick={(e) => {
                  e.stopPropagation();
                  openVideoViewer(0);
                }}
              >
                <span aria-hidden>📹</span>
                <span className="truncate">{language === "ru" ? "смотреть видео" : "watch video"}</span>
              </div>
            )}
          </div>
        </div>

        {isViewerOpen && (
          <div 
            class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            onClick={closeImageViewer} // Close viewer if clicked outside the image and indices
          >
            <button 
              class="absolute top-4 right-4 text-white text-2xl" 
              onClick={(e) => {
                e.stopPropagation(); // Prevent event bubbling
                closeImageViewer();
              }}
            >
              &times;
            </button>
            <div 
              class="relative md:max-h-[80%] md:max-w-[80%] flex flex-col items-center justify-center"
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on the image or indices
            >
              <div 
                class="absolute inset-y-0 left-0 w-1/2 cursor-pointer"
                onClick={showPreviousImage} // Navigate left if clicked on the left half of the image
              />
              <img 
                class="max-h-full max-w-full w-full md:w-auto" 
                src={images[currentImageIndex]} 
                alt={`Art Image ${currentImageIndex + 1}`}
              />
              <div 
                class="absolute inset-y-0 right-0 w-1/2 cursor-pointer"
                onClick={showNextImage} // Navigate right if clicked on the right half of the image
              />
              <div class="mt-2 text-white text-lg text-center w-full">
                {`${currentImageIndex + 1} / ${images.length}`} {/* Display current image index and total */}
              </div>
            </div>
          </div>
        )}
        {isVideoViewerOpen && (
          <div 
            class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            onClick={closeVideoViewer}
          >
            <button 
              class="absolute top-4 right-4 text-white text-2xl" 
              onClick={(e) => {
                e.stopPropagation();
                closeVideoViewer();
              }}
            >
              &times;
            </button>

            <div 
              class="relative md:max-h-[80%] md:max-w-[80%] flex flex-col items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* <div 
                class="absolute inset-y-0 left-0 w-1/2 cursor-pointer"
                onClick={showPreviousVideo}
              /> */}
              <div class="max-h-full max-w-full w-full md:w-auto flex items-center justify-center">
                {(() => {
                  const current = videos[currentVideoIndex];
                  const ytEmbed = getYouTubeEmbedUrl(current);
                  if (ytEmbed) {
                    const watchUrl = ytEmbed.replace("/embed/", "/watch?v=");
                    return (
                      <a
  href={watchUrl}
  target="_blank"
  rel="noopener noreferrer"
  className="text-gray-200 font-semibold text-xl hover:text-white hover:shadow-md transition-all duration-200"
>
  {language === "ru" ? "Открыть в YouTube" : "Open in YouTube"}
</a>
                    )

                    // YouTube embed
                    return (
                      <iframe
                        title={`video-${currentVideoIndex}`}
                        src={ytEmbed}
                        className="w-full md:w-auto max-h-full max-w-full"
                        style={{ minHeight: '200px' }}
                        frameBorder="0"
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                      />
                    );
                  } else if (isDirectVideoFile(current)) {
                    // direct video file
                    return (
                      <video
                        className="max-h-full max-w-full w-full md:w-auto"
                        src={current}
                        controls
                        autoPlay
                      />
                    );
                  } else {
                    // fallback: try to embed URL in an iframe (some sites allow embedding)
                    return (
                      <iframe
                        title={`video-fallback-${currentVideoIndex}`}
                        src={current}
                        className="w-full md:w-auto max-h-full max-w-full"
                        style={{ minHeight: '200px' }}
                        frameBorder="0"
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                      />
                    );
                  }
                })()}
              </div>

              {/* <div 
                class="absolute inset-y-0 right-0 w-1/2 cursor-pointer"
                onClick={showNextVideo}
              /> */}
              {/* <div class="mt-2 text-white text-lg text-center w-full">
                {videos && videos.length > 0 ? `${currentVideoIndex + 1} / ${videos.length}` : ""}
              </div> */}
            </div>
          </div>
        )}

        <div class="text-3xl flex flex-wrap items-center justify-center text-center gap-2">
          <b>{localizeArt(art, language).name + formatYear(art.year, language)}</b>
          <Button
            variant="light"
            color="teal"
            size="sm"
            radius="md"
            onClick={handleCopyLink}
          >
            🔗{isCopyLinkCopied ? " ✔" : ""}
          </Button>
        </div>
        <div class="my-1 flex justify-center text-center space-x-2 md:space-x-3">
          <PlatformRatings art={art} fullMode={true} />
        </div>

        {
        art.watchLink &&
        <div class="flex justify-center text-center space-x-2 md:space-x-3 mt-2">
          <a 
            href={art.watchLink} 
            target="_blank" 
            rel="noopener noreferrer"
            class="text-blue-500 hover:underline"
          >
            {language === "ru" ? "Смотреть" : "Watch"}
          </a>
        </div>
        }


        
        <div class="mx-auto w-full max-w-2xl px-4 md:px-6">
          <div class="my-2 flex flex-col items-center text-center space-y-1">
            {
            art.subCategory === "series" ? (
              <>
                <div class="text-gray-600">
                  {language === "ru" ? "Сериал" : "TV series"}
                  {art.year && (
                    language === "ru" ? ", вышел в " + art.year : ", released in " + art.year
                  )}
                </div>
                <div class="text-gray-600">
                  {art.lastEpisodeTime && new Date(art.lastEpisodeTime).getTime() !== 0 && (
                    language === "ru" ? "Последняя серия: " + formatDate(art.lastEpisodeTime, language) : "Last episode: " + formatDate(art.lastEpisodeTime, language)
                  )}<br/>
                  {art.nextEpisodeTime && new Date(art.nextEpisodeTime).getTime() !== 0 && (
                    language === "ru" ? "Следующая серия: " + formatDate(art.nextEpisodeTime, language) : "Next episode: " + formatDate(art.nextEpisodeTime, language)
                  )}
                </div>
              </>
            ) : (
              art.category === "3" && (
                <div class="text-gray-600">
                  {art.subCategory}
                </div>
              ) 
            )
            }
            {localizeArt(art, language).genres && (
              <div class="text-gray-600">
                <b>{language === "ru" ? "Жанр:" : "Genre:"}</b> {localizeArt(art, language).genres.join(", ")}
              </div>
            )}
            {art.duration !== 0 && (
              <div class="text-gray-600">
                <b>{language === "ru" ? "Длительность:" : "Duration:"}</b> {formatDuration(art.duration, language)}
              </div>
            )}
            {art.videoGameDeveloper && (
                <div class="text-gray-600">
                  <b>
                    {art.category === 1
                      ? language === "ru" ? "Режиссер:" : "Director:"
                      : art.category === 2
                      ? language === "ru" ? "Разработчик:" : "Developer:"
                      : language === "ru" ? "Автор:" : "Author:"}
                  </b> 
                  <Link class="text-gray-400 hover:text-gray-300" to={'/addEvent?limit=200&term=author:'+encodeURIComponent(localizeArt(art, language).videoGameDeveloper)}>
                    &nbsp;{localizeArt(art, language).videoGameDeveloper}
                  </Link>
                </div>
            )}
            {localizeArt(art, language).actors && (
              <div className="text-gray-600">
                <b>{language === "ru" ? "Актёры:" : "Actors:"}</b>{" "}
                {localizeArt(art, language).actors.map((actor, index) => (
                  <span key={actor}>
                    <Link class="text-gray-400 hover:text-gray-300" to={'/addEvent?limit=200&term=actor:'+encodeURIComponent(actor)}>
                      {actor}
                    </Link>
                    {index < localizeArt(art, language).actors.length - 1 && ", "}
                  </span>
                ))}
              </div>
            )}
            {localizeArt(art, language).country && (
              <div class="text-gray-600">
                <b>{language === "ru" ? "Страна:" : "Country:"}</b> {countries.getName(localizeArt(art, language).country, language) || localizeArt(art, language).country}
              </div>
            )}
          </div>
        </div>



        <div class="mx-auto w-full max-w-2xl px-4 md:px-6">
          <div class="flex justify-center text-center whitespace-pre-wrap text-white">
            {
            language === "ru" 
              ? (art.ruDescription !== '' ? renderDescription(art.ruDescription) : "Описание готовится...")
              : (art.description !== '' ? renderDescription(art.description) : "Description is being prepared...")
            }
          </div>
        </div>

        <div class="divide-y border-t border-b divide-gray-300 mt-4 mb-4">
          <div class="mt-4 mb-4 text-xl">
            {
            myUser
            ?
            <div class="">
              <CardEventArtChatMini art={art} />

              <div class="flex justify-center text-center mt-2 mb-1 space-x-8">
                <div class="flex justify-center text-center relative">
                  <img
                    src="/send_plane_icon.png"
                    alt={language === "ru" ? "Поиск людей" : "Person search"}
                    title={language === "ru" ? "Рекомендовать друзьям" : "Recommend to friends"}
                    className="inline-block w-9 h-9 cursor-pointer"
                    onClick={() => setIsDropdownVisible((prev) => !prev)}
                  />
                  {art.otherPeopleEvents && art.otherPeopleEvents.length > 0 && (
                    <span class="absolute bottom-0 right-0 w-2 h-2 bg-red-500 rounded-full border-2 border-red-500"></span>
                  )}
                  <FollowingDropdown
                    isOpen={isDropdownVisible}
                    onClose={() => setIsDropdownVisible(false)}
                    language={language}
                    art={art}
                  />
                </div>

                <div class="flex justify-center text-center">
                  <img
                    src="/calendar_icon_2.png"
                    alt={language === "ru" ? "Журнал" : "Logbook"}
                    title={language === "ru" ? "Указать даты" : "Specify dates"}
                    className="inline-block w-9 h-9 cursor-pointer"
                    onClick={() => setIsLogbookDropdownVisible((prev) => !prev)} // Toggle dropdown visibility
                  />
                  {isLogbookDropdownVisible && (
                    <LogbookDropdown
                      artId={art.id}
                      userId={myUser.myUserInfo.myUserId}
                      onClose={() => setIsLogbookDropdownVisible(false)} // Close dropdown
                      isOpen = {isLogbookDropdownVisible}
                      language={language}
                      initialLogbookDateTimes={initialLogbookDateTimes} // Pass pre-loaded logbook date times, if present then artId and userId are not needed
                      initialArt={art} // Pre-loaded art data, if present then artId and userId are not needed
                    />
                  )}
                </div>
              </div>
            </div>
            :
              <div class="mt-4 flex justify-center text-center items-center">
                {
                  language === "ru" ?
                  <div>
                    <div class="flex justify-center text-center mb-4 font-medium">
                      Залогиньтесь и добавьте в свою библиотеку
                    </div>
                    <div class="flex justify-center">
                      <div onClick={handleLoginWithGoogle} class="w-4/5 md:w-2/3 flex px-4 items-center hover:cursor-pointer border border-black rounded-full text-lg">
                        <img src="/google-login-image_no_text.png" />
                        Войти через Google
                      </div>
                    </div>
                  </div>                
                  :
                  <div>
                    <div class="flex justify-center text-center mb-4 font-medium">
                      Log in and add to your library
                    </div>
                    <div class="flex justify-center">
                      <img class="w-4/5 md:w-1/3" onClick={handleLoginWithGoogle} src="/google-login-image.png" />
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>

        <div class="mt-4 font-semibold text-lg flex justify-center text-center">
          {language === "ru" ? "Похожие" : "Similar"}
        </div>
        {
        similars !== null && similars.length > 0
        ?
          <div className="w-full">
            <ImageScroller key={art.id} arts={similars} />
          </div>
        :
          similars !== null
          ?
            <div class="flex justify-center text-center mb-15">
              {language === "ru" ? "Вычисляются (занимает 10-15 секунд)..." : "Being calculated (takes 10-15 sec)..."}
              {similars !== null && similars.length === 0 && (
                <button 
                  onClick={() => fetchSimilars(id)}
                  class="ml-2 px-2 py-1 border rounded text-sm"
                >
                  {language === "ru"
                  ? 
                    (loadingSimilarsManual ? '...' : "↻")
                  : 
                    (loadingSimilarsManual ? '...' : "↻")
                  }
                </button>
              )}
            </div>
          :
            <div class="flex justify-center text-center">
              <div class="w-full max-w-md">
                <div class="h-2 w-full overflow-hidden rounded-full bg-gray-700">
                  <div class="similars-progress-bar h-full w-1/3 rounded-full bg-gray-300"></div>
                </div>
              </div>
            </div>
        }
    </div>
  );
}

export default ArtPage;
